(() => {
  function parseSeconds(value, fallbackSeconds = 30) {
    if (!value) return fallbackSeconds;
    const v = String(value).trim();
    if (!v) return fallbackSeconds;
    try {
      if (v.endsWith('ms')) return Math.max(0.001, parseFloat(v) / 1000);
      if (v.endsWith('s')) return Math.max(0.001, parseFloat(v));
      const n = parseFloat(v);
      return isNaN(n) ? fallbackSeconds : n;
    } catch (e) {
      return fallbackSeconds;
    }
  }

  function initTicker(sectionEl) {
    const marquee = sectionEl.querySelector('.testimonials-marquee');
    if (!marquee) return;
    const track = marquee.querySelector('.testimonials-marquee__track');
    if (!track) return;

    // Disable CSS animation; JS will drive transforms
    track.style.animation = 'none';

    const originals = Array.from(track.children);
    originals.forEach((el) => el.setAttribute('data-original', '1'));

    let tileWidth = 0;
    let sequenceWidth = 0;
    let gapPx = 18;
    let offsetX = 0;
    let raf = 0;
    let lastTs = 0;
    let paused = false;
    let dragging = false;
    let startX = 0;
    let baseOffset = 0;

    function getGap() {
      const cs = getComputedStyle(track);
      return parseFloat(cs.columnGap || cs.gap || '18') || 18;
    }

    function measureSequenceWidth() {
      // Sum widths of original children + gaps between them
      const items = originals;
      const gap = gapPx;
      let total = 0;
      items.forEach((el) => { total += el.offsetWidth; });
      const internalGaps = Math.max(0, items.length - 1) * gap;
      return total + internalGaps;
    }

    // Static speed in pixels per second (tune as desired)
    const STATIC_PX_PER_SEC = 50; // lower = slower

    function getDirectionMultiplier() {
      // normal => left (-1), reverse => right (+1)
      const dirVar = getComputedStyle(marquee).getPropertyValue('--direction').trim();
      return dirVar === 'reverse' ? 1 : -1;
    }

    function populateClones() {
      // Remove previous clones
      Array.from(track.children).forEach((el) => { if (!el.hasAttribute('data-original')) el.remove(); });

      const containerW = marquee.clientWidth || 0;
      gapPx = getGap();
      sequenceWidth = measureSequenceWidth();
      tileWidth = sequenceWidth + gapPx; // include one gap between sequences

      // Ensure enough content to cover container plus spare for wrapping
      let needed = Math.max(containerW + tileWidth * 2, tileWidth * 3);
      let current = 0;
      while (current < needed) {
        for (const el of originals) {
          const clone = el.cloneNode(true);
          clone.removeAttribute('data-original');
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        }
        current = track.scrollWidth;
      }

      // Normalize offset into [-tileWidth, 0)
      offsetX = ((offsetX % tileWidth) + tileWidth) % tileWidth; // now 0..tileWidth
      offsetX = -offsetX;
      applyTransform();
    }

    function applyTransform() {
      track.style.transform = `translateX(${offsetX}px)`;
    }

    function tick(ts) {
      raf = requestAnimationFrame(tick);
      if (paused || dragging) return;
      if (!lastTs) { lastTs = ts; return; }
      const dt = Math.min(0.05, (ts - lastTs) / 1000); // clamp to avoid jumps
      lastTs = ts;
      const dir = getDirectionMultiplier();
      const pxPerSec = STATIC_PX_PER_SEC;
      offsetX += dir * pxPerSec * dt;
      // Wrap into [-tileWidth, 0)
      while (offsetX <= -tileWidth) offsetX += tileWidth;
      while (offsetX > 0) offsetX -= tileWidth;
      applyTransform();
    }

    function start() {
      stop();
      lastTs = 0;
      raf = requestAnimationFrame(tick);
    }

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    function onResize() {
      const f = tileWidth ? (-offsetX / tileWidth) : 0; // 0..1
      populateClones();
      offsetX = -f * tileWidth;
      applyTransform();
    }

    // No debouncer: apply resize updates immediately

    function onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      marquee.classList.add('is-dragging');
      marquee.style.userSelect = 'none';
      try { marquee.setPointerCapture(e.pointerId); } catch (_) {}
      startX = e.clientX;
      baseOffset = offsetX;
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp, { once: true });
      window.addEventListener('pointercancel', onPointerUp, { once: true });
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const delta = e.clientX - startX;
      offsetX = baseOffset + delta;
      while (offsetX <= -tileWidth) offsetX += tileWidth;
      while (offsetX > 0) offsetX -= tileWidth;
      applyTransform();
    }

    function onPointerUp(e) {
      dragging = false;
      marquee.classList.remove('is-dragging');
      marquee.style.userSelect = '';
      try { marquee.releasePointerCapture(e.pointerId); } catch (_) {}
      window.removeEventListener('pointermove', onPointerMove);
    }

    // Pause on hover if enabled
    const pauseable = marquee.classList.contains('testimonials-marquee--pauseable');
    if (pauseable) {
      marquee.addEventListener('mouseenter', () => { paused = true; });
      marquee.addEventListener('mouseleave', () => { paused = false; });
    }

    // Drag support
    marquee.addEventListener('pointerdown', onPointerDown);

    // Setup
    populateClones();
    start();

    // Resize observer
    const onWinResize = () => onResize();
    window.addEventListener('resize', onWinResize);

    // Cleanup if needed (Theme Editor may re-render)
    sectionEl.addEventListener('shopify:section:unload', () => {
      stop();
      window.removeEventListener('resize', onWinResize);
    });
  }

  function initAll(root = document) {
    const sections = root.querySelectorAll('.testimonials-v2-section');
    sections.forEach(initTicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Shopify Theme Editor support
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target || e.detail?.section;
    if (section) initAll(section);
  });
})();
